import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    ButtonRow,
    Card,
    FormErrorMessage,
    FormParagraph,
    IconButton,
    Link,
    PageHeader,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { Link as ReactRouterLink } from '@ksyos/router';
import { capitalize, uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEmploymentsToUpdateValidated } from '@common/validation/employment';
import type { IEmployment, IManagerRole } from '@frontend/components/interfaces/';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums/';
import { getLanguage } from '@common/translate/translate';
import { getDisplayName } from '@common/utils/getDisplayName';
import { employmentsToUpdateSchema } from '@common/validation/employment';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import {
    assertHasAuthorization,
    hasOneAuthorization,
    scrollToValidationError,
    useAbortableFetch,
} from '@frontend/utils/';
import { validate } from '@frontend/utils/validation';
import { OrganizationProfile } from '../organizations/OrganizationProfile';

interface IKsyosOrganizationProps {
    organizationId: string;
}

export function KsyosOrganization(props: IKsyosOrganizationProps) {
    const abortableFetch = useAbortableFetch();
    const hasAuthorizationAdminAddEdit = hasOneAuthorization(ManagerAuthorization.adminsAddEdit);
    const isKsyos = useSelector((state: IReduxState) => state.authentication.isKsyos);
    const translate = getLanguage();

    const [employments, setEmployments] = useState<IEmployment[]>([]);
    const [employmentsToUpdate, setEmploymentsToUpdate] = useState<string[]>([]);
    const [employmentsToDelete, setEmploymentsToDelete] = useState<string[]>([]);
    const [healthCareProfessionalEmployments, setHealthCareProfessionalEmployments] = useState<string[]>([]);
    const [managerRoles, setManagerRoles] = useState<IManagerRole[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [showModal, setShowModal] = useState(false);

    assertHasAuthorization(ManagerAuthorization.adminsRead);

    if (!isKsyos) {
        return null;
    }

    useEffect(() => {
        loadEmployments();
        if (hasAuthorizationAdminAddEdit) {
            loadManagerRoles();
        }
    }, []);

    return (
        <PageTemplate
            sideBarProfile={
                <OrganizationProfile
                    organizationId={props.organizationId}
                    size="large"
                />
            }
            topBarProfile={
                <OrganizationProfile
                    organizationId={props.organizationId}
                    size="small"
                />
            }
        >
            <PageHeader
                action={
                    hasAuthorizationAdminAddEdit ? (
                        <Button
                            as={ReactRouterLink}
                            to={`/organizations/${props.organizationId}/view/addEmployee`}
                            variant="outline"
                        >
                            {translate.organization.addStaffMember}
                        </Button>
                    ) : undefined
                }
                title={translate.organization.organizationOverview}
            />
            <Card>
                <Card.Header
                    action={
                        hasAuthorizationAdminAddEdit && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : (
                            <ButtonRow>
                                <CancelButton
                                    disabled={false}
                                    onCancel={exitEditMode}
                                />
                                <SaveButton
                                    onSave={async () =>
                                        employmentsToDelete.length > 0 ? setShowModal(true) : handleSubmit()
                                    }
                                />
                            </ButtonRow>
                        )
                    }
                    title={translate.organization.ksyosEmployees}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translate.common.name}</TableHeader>
                                <TableHeader>{capitalize(translate.common.role)}</TableHeader>
                                <TableHeader>{translate.common.lastModified}</TableHeader>
                                <TableHeader>{translate.common.lastLogin}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>{renderEmployments()}</TableBody>
                    </Table>
                </TableContainer>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
            </Card>

            {showModal && (
                <ConfirmationModal
                    cancelText={translate.common.cancelling}
                    confirmText={translate.common.removing}
                    handleNo={exitEditMode}
                    handleYes={handleSubmit}
                    isOpen={showModal}
                    question={translate.organization.modal.deactivateKsyosEmployment}
                    title={translate.common.areYouSure}
                />
            )}
        </PageTemplate>
    );

    function renderEmployments() {
        return employments.map((employment) => (
            <TableRow key={employment.id}>
                <TableCell
                    fontWeight="medium"
                    sx={{ display: isEditing ? 'flex' : 'table-cell' }}
                >
                    {isEditing && (
                        <IconButton
                            icon="delete"
                            onClick={async () => await handleDeleteEmployment(employment.id)}
                            style={{ marginRight: '10px' }}
                        />
                    )}
                    {employment.practitioner ? (
                        <Link
                            to={`/practitioners/${employment.practitioner.id}/profile`}
                            variant="link"
                        >
                            {getDisplayName(employment.practitioner, false, 'familyNameCommaFirstName')}
                        </Link>
                    ) : (
                        ''
                    )}
                </TableCell>
                <TableCell>
                    {!isEditing ? (
                        <FormParagraph>{employment.managerRole?.name ?? ''}</FormParagraph>
                    ) : (
                        <Box minWidth={220}>
                            <Select
                                className="automation-choose-manager-role"
                                isClearable={true}
                                onChange={(role) => handleChangeManagerRoles(employment.id, role)}
                                options={managerRoles.map((managerRole) => getManagerRoleOption(managerRole))}
                                placeholder={translate.practitioner.chooseRole}
                                value={employment.managerRole ? getManagerRoleOption(employment.managerRole) : null}
                            />
                        </Box>
                    )}
                    {isEditing && employmentHasNoRoles(employment) && (
                        <FormErrorMessage>{translate.practitioner.chooseAtLeastOneRole}</FormErrorMessage>
                    )}
                </TableCell>
                <TableCell>
                    {employment?.updated_at
                        ? DateTime.from(employment.updated_at).format('DD-MM-YYYY HH:mm')
                        : undefined}
                </TableCell>
                <TableCell>
                    {employment.practitioner?.lastLoginOn
                        ? DateTime.from(employment.practitioner.lastLoginOn).format('DD-MM-YYYY HH:mm')
                        : undefined}
                </TableCell>
            </TableRow>
        ));
    }

    function employmentHasNoRoles(employmentList: IEmployment | IEmployment[]) {
        const employmentIdList = (Array.isArray(employmentList) ? employmentList : [employmentList]).map(
            (employment) => employment.id
        );

        const employmentsToCheck = employments.filter((employment) => employmentIdList.includes(employment.id));

        return employmentsToCheck.some((employment) => !employment.managerRole?.id);
    }

    function getValidatedEmployments(employmentsToValidate: IEmployment[]) {
        const validatedEmploymentsToUpdate: IEmploymentsToUpdateValidated[] = [];

        for (const employment of employmentsToValidate) {
            const employmentToUpdate = { ...employment, managerRoleId: employment.managerRole?.id ?? null };
            const { errors, item } = validate(employmentsToUpdateSchema, employmentToUpdate);

            if (errors.size === 0) {
                validatedEmploymentsToUpdate.push(item as IEmploymentsToUpdateValidated);
            }
        }

        return validatedEmploymentsToUpdate;
    }

    function getManagerRoleOption(managerRole: IManagerRole) {
        return { label: managerRole.name, value: managerRole.id };
    }

    async function exitEditMode() {
        setEmploymentsToDelete([]);
        await loadEmployments();
        setShowModal(false);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (employmentHasNoRoles(employments)) {
            scrollToValidationError();
        } else {
            await updateEmployments();
            exitEditMode();
        }
    }

    function handleDeleteEmployment(employmentId: string) {
        const practitionerId = employments.find((employment) => employment.id === employmentId)?.practitioner?.id;
        if (!practitionerId) {
            return;
        }
        getAndSetHealthCareProfessionalEmployments(practitionerId);
        setEmployments(employments.filter((employment) => employment.id !== employmentId));
        setEmploymentsToDelete(uniq([...employmentsToDelete, employmentId]));
    }

    async function getAndSetHealthCareProfessionalEmployments(practitionerId: string) {
        const activeEmployments = (
            await abortableFetch.fetchJson(`/api/user/employments?practitionerId=${practitionerId}`, 'GET')
        ).json.items.map((employment: { id: string }) => employment.id);

        setHealthCareProfessionalEmployments(activeEmployments);
    }

    function handleChangeManagerRoles(employmentId: string, managerRole: { label: string; value: string } | null) {
        setEmploymentsToUpdate(uniq([...employmentsToUpdate, employmentId]));
        setEmployments(
            employments.map((employment) => {
                if (employment.id === employmentId) {
                    employment.managerRole = managerRole
                        ? { id: managerRole.value, name: managerRole.label }
                        : undefined;
                }

                return employment;
            })
        );
    }

    async function loadEmployments() {
        const url = '/api/ksyos/employments?isActive=true';
        const result = await abortableFetch.fetchJson(url, 'GET');

        setEmployments(result.json.items);
    }

    async function loadManagerRoles() {
        const url = '/api/ksyos/managerRoles';
        const result = await abortableFetch.fetchJson(url, 'GET');

        setManagerRoles(result.json.items);
    }

    async function updateEmployments() {
        const updateUrl = '/api/ksyos/employments/editRolesAndDeactivate';
        const filteredEmployments = employments.filter((employment) => employmentsToUpdate.includes(employment.id));
        const validatedEmployments = getValidatedEmployments(filteredEmployments);
        const filteredHealthCareProfessionalEmployments = healthCareProfessionalEmployments.filter(
            (employmentsId) => !employmentsToDelete.includes(employmentsId)
        );
        const body = {
            employmentsToDelete,
            employmentsToUpdate: validatedEmployments,
            filteredHealthCareProfessionalEmployments,
        };
        await abortableFetch.postJson(updateUrl, body);
    }
}
